/**
 * Shared Prop Type Filter Options
 * Canonical filter categories for leaderboard, picks, and analysis views
 */

export const PROP_TYPE_FILTER_OPTIONS = [
  { key: '', label: 'All Props' },
  { key: 'points', label: 'Points' },
  { key: 'rebounds', label: 'Rebounds' },
  { key: 'assists', label: 'Assists' },
  { key: 'threes', label: '3-Pointers' },
  { key: 'steals', label: 'Steals' },
  { key: 'blocks', label: 'Blocks' },
  { key: 'turnovers', label: 'Turnovers' },
  { key: 'combos', label: 'Combos' },
  { key: 'double_double', label: 'Double-Double' },
  { key: 'triple_double', label: 'Triple-Double' },
  { key: 'first_basket', label: 'First Basket' },
  { key: 'field_goals', label: 'Field Goals' },
  { key: 'free_throws', label: 'Free Throws' },
  { key: 'game', label: 'Game Props' },
  { key: 'team_totals', label: 'Team Totals' },
  { key: 'half', label: '1st/2nd Half' },
  { key: 'quarter', label: 'Quarters' },
] as const;

export type PropTypeFilterKey = (typeof PROP_TYPE_FILTER_OPTIONS)[number]['key'];

/** A real prop category (every filter key except the empty "All Props"). */
export type PropCategory = Exclude<PropTypeFilterKey, ''>;

/**
 * Maps an Odds API market key to the leaderboard prop category it belongs to.
 * The SSOT for how per-leg market keys roll up into the filter categories, used
 * by the stats writer (attribute a parlay to a category) and any category label
 * lookup. Base and `_alternate` variants map to the same category so a main and
 * alternate line count as one category.
 */
export const MARKET_KEY_TO_CATEGORY: Record<string, PropCategory> = {
  player_points: 'points',
  player_points_alternate: 'points',
  player_rebounds: 'rebounds',
  player_rebounds_alternate: 'rebounds',
  player_assists: 'assists',
  player_assists_alternate: 'assists',
  player_threes: 'threes',
  player_threes_alternate: 'threes',
  player_steals: 'steals',
  player_steals_alternate: 'steals',
  player_blocks: 'blocks',
  player_blocks_alternate: 'blocks',
  player_turnovers: 'turnovers',
  player_turnovers_alternate: 'turnovers',
  player_points_rebounds_assists: 'combos',
  player_points_rebounds_assists_alternate: 'combos',
  player_points_rebounds: 'combos',
  player_points_rebounds_alternate: 'combos',
  player_points_assists: 'combos',
  player_points_assists_alternate: 'combos',
  player_rebounds_assists: 'combos',
  player_rebounds_assists_alternate: 'combos',
  player_blocks_steals: 'combos',
  player_blocks_steals_alternate: 'combos',
  player_double_double: 'double_double',
  player_double_double_alternate: 'double_double',
  player_triple_double: 'triple_double',
  player_triple_double_alternate: 'triple_double',
  player_first_basket: 'first_basket',
  player_first_team_basket: 'first_basket',
  player_field_goals: 'field_goals',
  player_field_goals_alternate: 'field_goals',
  player_free_throws: 'free_throws',
  player_frees_made: 'free_throws',
  player_frees_made_alternate: 'free_throws',
  spreads: 'game',
  totals: 'game',
  h2h: 'game',
  alternate_spreads: 'game',
  alternate_totals: 'game',
  team_totals: 'team_totals',
  alternate_team_totals: 'team_totals',
};

/**
 * Resolve a market key to its prop category, or null when it belongs to none.
 * Period markets (half/quarter) are matched by suffix so every present and
 * future `_h1`/`_q3`/`player_points_q1` key routes without an explicit entry.
 */
export function marketKeyToCategory(marketKey: string): PropCategory | null {
  const direct = MARKET_KEY_TO_CATEGORY[marketKey];
  if (direct) return direct;
  if (/_(h1|h2)$/.test(marketKey)) return 'half';
  if (/_q[1-4]$/.test(marketKey)) return 'quarter';
  return null;
}

/** Display label for a stored category id (e.g. 'game' -> 'Game Props'). */
export function getCategoryLabel(categoryId: string): string {
  return PROP_TYPE_FILTER_OPTIONS.find((o) => o.key === categoryId)?.label ?? categoryId;
}
